<template>
  <RangeCalendarRoot v-slot="{ grid, weekDays }" data-slot="range-calendar"
    :class="cn('p-3', props.class)" v-bind="forwarded">
    <RangeCalendarHeader>
      <RangeCalendarHeading v-slot="{ headingValue }">
        <div class="flex items-center  justify-center">
          <!-- Bouton année précédente -->
          <RangeCalendarPrev type="button" :class="[buttonClasses]"
            :prev-page="(date: DateValue) => pagingFunc(date, -1)">
            <ChevronDoubleLeftIcon class="h-4 w-4" />
          </RangeCalendarPrev>

          <RangeCalendarPrev :class="buttonClasses">
            <ChevronLeftIcon class="h-4 w-4" />
          </RangeCalendarPrev>

          <!-- Affichage mois/année actuel -->
          <div class="text-sm font-medium min-w-[120px] text-center">
            {{ headingValue }}
          </div>

          <RangeCalendarNext :class="buttonClasses">
            <ChevronRightIcon class="h-4 w-4" />
          </RangeCalendarNext>

          <!-- Bouton année suivante -->
          <RangeCalendarNext type="button" :class="[buttonClasses]"
            :next-page="(date: DateValue) => pagingFunc(date, 1)">
            <ChevronDoubleRightIcon class="h-4 w-4" />
          </RangeCalendarNext>
        </div>
      </RangeCalendarHeading>
    </RangeCalendarHeader>

    <div class="flex flex-col gap-y-4 mt-4 sm:flex-row sm:gap-x-4 sm:gap-y-0">
      <RangeCalendarGrid v-for="month in grid" :key="month.value.toString()">
        <RangeCalendarGridHead>
          <RangeCalendarGridRow>
            <RangeCalendarHeadCell v-for="day in weekDays" :key="day">
              {{ day }}
            </RangeCalendarHeadCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridHead>
        <RangeCalendarGridBody>
          <RangeCalendarGridRow v-for="(weekDates, index) in month.rows"
            :key="`weekDate-${index}`" class="mt-2 w-full">
            <RangeCalendarCell v-for="weekDate in weekDates"
              :key="weekDate.toString()" :date="weekDate">
              <RangeCalendarCellTrigger :day="weekDate" :month="month.value" />
            </RangeCalendarCell>
          </RangeCalendarGridRow>
        </RangeCalendarGridBody>
      </RangeCalendarGrid>
    </div>
  </RangeCalendarRoot>
</template>
<script lang="ts" setup>
import type { DateValue, RangeCalendarRootEmits, RangeCalendarRootProps } from "reka-ui"
import { RangeCalendarPrev, RangeCalendarNext, RangeCalendarRoot, useForwardPropsEmits } from "reka-ui"

import { computed, type HTMLAttributes } from "vue"
import { reactiveOmit } from "@vueuse/core"
import { cn } from "@/lib/utils"
import { RangeCalendarCell, RangeCalendarCellTrigger, RangeCalendarGrid, RangeCalendarGridBody, RangeCalendarGridHead, RangeCalendarGridRow, RangeCalendarHeadCell, RangeCalendarHeader, RangeCalendarHeading } from "."
import { ChevronDoubleLeftIcon, ChevronDoubleRightIcon, ChevronLeftIcon, ChevronRightIcon } from "@heroicons/vue/24/outline"

const props = defineProps<RangeCalendarRootProps & { class?: HTMLAttributes["class"] }>()

const emits = defineEmits<RangeCalendarRootEmits>()

const delegatedProps = reactiveOmit(props, "class")

const forwarded = useForwardPropsEmits(delegatedProps, emits)

function pagingFunc(date: DateValue, sign: -1 | 1) {
  if (sign === -1)
    return date.subtract({ years: 1 })


  let futureDate = date.add({ years: 1 })

  // if future date is abover max date, check if the current page is the last
  // If it is the last page, return the future date to keep the button disable
  // Else, set the future date to the max date to jump to the last page

  if (props.maxValue && futureDate > props.maxValue) {
    // check if last page
    if (futureDate.month == props.maxValue.month) {
      return futureDate;
    }

    return props.maxValue;
  }

  // if (props.maxValue && futureDate > props.maxValue)
  //   futureDate = props.maxValue


  return futureDate
}

const buttonClasses = computed(() => {
  return ["inline-flex",
    "items-center",
    "cursor-pointer",
    "text-black",
    "justify-center",
    "rounded-[9px]",
    "bg-transparent",
    "w-8",
    "h-8",
    "hover:bg-black",
    "hover:text-white",
    "active:scale-98",
    "active:transition-all",
    "focus:shadow-[0_0_0_2px] focus:shadow-black",
    // Styles pour les boutons désactivés
    "disabled:opacity-30",
    "disabled:cursor-not-allowed",
    "disabled:hover:bg-transparent",
    "disabled:hover:text-black",
    "disabled:active:scale-100"]
})
</script>

<style scoped>
@reference "../../../style.css"

.disabled-button {
  @apply bg-gray-100 text-gray-400 cursor-not-allowed opacity-60;
  transition: none !important;
}

.disabled-button:hover {
  @apply bg-gray-100 text-gray-400 scale-100;
}

.disabled-button:active {
  @apply scale-100;
}

.disabled-button:focus {
  @apply shadow-none;
}
</style>